import { Link } from 'react-router'
import {
  Mtds,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  AppLayout,
  useGetProductsQuery,
  useGetMatricesQuery,
  useGetSubstancesQuery,
  useGetLabsQuery,
  useGetAllTeamsQuery,
  useGetNewsArticlesQuery
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { adminNavigation } from '../navigation'

type NavItem = (typeof adminNavigation)[number]['items'][number]

type CountQuery = {
  data: unknown[] | undefined
  isError: boolean
  refetch: () => unknown
}

const CONTENT_GROUP_LABEL = 'Registrert innhold'

const NavCard = ({ item, count }: { item: NavItem; count?: CountQuery }) => {
  const { to, label, description, icon: Icon } = item

  return (
    <Mtds.Card as={Link} to={to}>
      <Mtds.Grid data-gap='2'>
        <Mtds.Heading as='h3' data-size='xs'>
          <Icon aria-hidden />
          {label}
        </Mtds.Heading>
        {count &&
          (count.isError ? (
            <Mtds.Muted>Antall utilgjengelig</Mtds.Muted>
          ) : count.data == null ? (
            <Mtds.Muted>Laster…</Mtds.Muted>
          ) : (
            <Mtds.Heading as='p' data-size='xl'>
              {count.data.length}
            </Mtds.Heading>
          ))}
        <Mtds.Muted>{description}</Mtds.Muted>
      </Mtds.Grid>
    </Mtds.Card>
  )
}

export const HomeScreen = () => {
  const products = useGetProductsQuery()
  const matrices = useGetMatricesQuery()
  const substances = useGetSubstancesQuery()
  const labs = useGetLabsQuery()
  const teams = useGetAllTeamsQuery()
  const news = useGetNewsArticlesQuery()
  const countQueries: Record<string, CountQuery> = {
    '/products': products,
    '/matrices': matrices,
    '/substances': substances,
    '/labs': labs,
    '/teams': teams,
    '/news': news
  }
  const failedCounts = Object.values(countQueries).filter(
    ({ isError }) => isError
  )
  const sections = adminNavigation.flatMap((group) => {
    if (group.label === 'Administrasjon') {
      return [
        {
          label: CONTENT_GROUP_LABEL,
          items: group.items.filter(({ to }) => to in countQueries)
        },
        {
          label: group.label,
          items: group.items.filter(({ to }) => !(to in countQueries))
        }
      ]
    }
    return group.label === 'Utviklerverktøy' ? [group] : []
  })

  return (
    <AppLayout
      isFullWidth
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      topBar={<TopBar logo={<AppLogo />} title='Oversikt' />}
    >
      <ScreenLayout
        header={
          <ScreenHeader
            title='Oversikt'
            description={
              <p>
                Administrer grunndata, organisasjon og innhold i
                Prøve-applikasjonene.
              </p>
            }
          />
        }
      >
        <Mtds.Grid data-gap='8'>
          {sections.map((section) => (
            <Mtds.Grid key={section.label} data-gap='4'>
              <Mtds.Heading as='h2' data-size='sm'>
                {section.label}
              </Mtds.Heading>
              <Mtds.Group>
                <Mtds.Grid data-gap='3'>
                  <Mtds.Grid data-items='300' data-gap='3'>
                    {section.items.map((item) => (
                      <NavCard
                        key={item.to}
                        item={item}
                        count={countQueries[item.to]}
                      />
                    ))}
                  </Mtds.Grid>
                  {section.label === CONTENT_GROUP_LABEL &&
                    failedCounts.length > 0 && (
                      <Mtds.Button
                        data-variant='tertiary'
                        onClick={() =>
                          failedCounts.forEach((query) => void query.refetch())
                        }
                      >
                        Prøv å hente antall på nytt
                      </Mtds.Button>
                    )}
                </Mtds.Grid>
              </Mtds.Group>
            </Mtds.Grid>
          ))}
        </Mtds.Grid>
      </ScreenLayout>
    </AppLayout>
  )
}
